import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { FiLogOut, FiGrid, FiStar, FiGlobe, FiChevronDown } from 'react-icons/fi';

const LANGUAGES = [
    { code: 'en', label: 'English', native: 'English' },
    { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
    { code: 'mr', label: 'Marathi', native: 'मराठी' },
    { code: 'gu', label: 'Gujarati', native: 'ગુજરાતી' },
    { code: 'ta', label: 'Tamil', native: 'தமிழ்' },
    { code: 'te', label: 'Telugu', native: 'తెలుగు' },
    { code: 'kn', label: 'Kannada', native: 'ಕನ್ನಡ' },
    { code: 'ml', label: 'Malayalam', native: 'മലയാളം' },
    { code: 'bn', label: 'Bengali', native: 'বাংলা' },
    { code: 'pa', label: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
    { code: 'ur', label: 'Urdu', native: 'اردو' },
    { code: 'fr', label: 'French', native: 'Français' },
    { code: 'de', label: 'German', native: 'Deutsch' },
    { code: 'es', label: 'Spanish', native: 'Español' },
    { code: 'ar', label: 'Arabic', native: 'العربية' },
    { code: 'zh-CN', label: 'Chinese', native: '中文' },
    { code: 'ja', label: 'Japanese', native: '日本語' },
    { code: 'ru', label: 'Russian', native: 'Русский' },
];

/* ── Set the googtrans cookie so GT picks up the language ── */
function setGTCookie(code) {
    const host = window.location.hostname;
    if (code === 'en') {
        // Clear all googtrans cookies to restore page default language (English)
        document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${host}`;
        if (host.includes('.')) {
            const parent = host.split('.').slice(-2).join('.');
            document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${parent}`;
        }
    } else {
        const value = `/en/${code}`;
        document.cookie = `googtrans=${value};path=/`;
        document.cookie = `googtrans=${value};path=/;domain=${host}`;
        if (host.includes('.')) {
            const parent = host.split('.').slice(-2).join('.');
            document.cookie = `googtrans=${value};path=/;domain=.${parent}`;
        }
    }
}

function changeLang(code) {
    setGTCookie(code); // persist across reloads
    window.location.reload(); // apply immediately and reliably via high-speed page reload
}

/* ── Read the googtrans cookie and return the matching language ── */
function getActiveLangFromCookie() {
    try {
        const match = document.cookie
            .split('; ')
            .find(row => row.startsWith('googtrans='));
        if (!match) return LANGUAGES[0];
        // cookie value is like "/en/hi" or "/en/zh-CN"
        const code = match.split('=')[1].split('/').pop();
        return LANGUAGES.find(l => l.code === code) || LANGUAGES[0];
    } catch {
        return LANGUAGES[0];
    }
}

export default function Navbar() {
    const { user, logout } = useAuth();
    const { isDark } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);         // hamburger on mobile
    const [scrolled, setScrolled] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const [activeLang, setActiveLang] = useState(() => getActiveLangFromCookie());
    const [isTranslating, setIsTranslating] = useState(false);
    const [translationProgress, setTranslationProgress] = useState(0);
    const [targetLang, setTargetLang] = useState(null);

    // Handle Translation Progress Simulation
    useEffect(() => {
        if (!isTranslating || !targetLang) return;

        let currentProgress = 0;
        const duration = 1000; // 1 second smooth transition concurrent with translation
        const step = 20; // update every 20ms
        const totalSteps = duration / step;
        const increment = 100 / totalSteps;

        const timer = setInterval(() => {
            currentProgress += increment;
            if (currentProgress >= 100) {
                clearInterval(timer);
                setTranslationProgress(100);

                // Hide the top progress bar quickly after 100% completion
                setTimeout(() => {
                    setIsTranslating(false);
                    setTargetLang(null);
                    setTranslationProgress(0);
                }, 200);
            } else {
                setTranslationProgress(Math.min(99, Math.floor(currentProgress)));
            }
        }, step);

        return () => clearInterval(timer);
    }, [isTranslating, targetLang]);

    // Close language dropdown on outside click
    // Must also allow clicks inside the mobile language section (#gt-pill OR .nav-lang-mobile)
    useEffect(() => {
        if (!langOpen) return;
        const handler = (e) => {
            const insideDesktop = e.target.closest('#gt-pill');
            const insideMobile = e.target.closest('.nav-lang-mobile');
            if (!insideDesktop && !insideMobile) setLangOpen(false);
        };
        document.addEventListener('mousedown', handler);
        document.addEventListener('touchstart', handler, { passive: true });
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('touchstart', handler);
        };
    }, [langOpen]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Close mobile menu on route change
    useEffect(() => { setOpen(false); }, [location.pathname]);

    const handleLogout = () => { logout(); navigate('/'); setOpen(false); };
    const handleLangSelect = (lang) => {
        setLangOpen(false);
        setOpen(false); // Close mobile hamburger too if open
        if (lang.code === activeLang.code) return;

        // Apply language changes IMMEDIATELY so the translation is instant
        setActiveLang(lang);
        changeLang(lang.code);

        // Start top progress bar animation concurrently
        setTargetLang(lang);
        setIsTranslating(true);
        setTranslationProgress(0);
    };

    const navBg = scrolled
        ? isDark ? 'rgba(8,14,26,0.97)' : 'rgba(255,255,255,0.97)'
        : isDark ? 'rgba(8,14,26,0.7)' : 'rgba(255,255,255,0.85)';

    const navBorder = scrolled
        ? isDark ? '1px solid rgba(79,110,247,0.12)' : '1px solid rgba(79,110,247,0.1)'
        : 'none';

    const T = { text: isDark ? '#f1f5f9' : '#1e293b', muted: isDark ? '#94a3b8' : '#64748b', surface: isDark ? '#111d33' : '#ffffff', border: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(79,110,247,0.15)' };

    return (
        <>
        {/* Sleek Top Brand Progress Bar (No Centering Popup Modal) */}
        {isTranslating && (
            <>
            {/* Invisible Click Shield to block interaction while loading */}
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'transparent',
                zIndex: 99998,
                cursor: 'wait',
            }} />
            
            {/* Glowing Brand Gold Progress Line at the very top */}
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(206, 173, 128, 0.1)',
                zIndex: 99999,
                overflow: 'hidden',
            }}>
                <div style={{
                    height: '100%',
                    width: `${translationProgress}%`,
                    background: 'linear-gradient(90deg, #a07e4a, #cead80, #e8d4a8)',
                    boxShadow: '0 0 10px rgba(206, 173, 128, 0.8), 0 0 5px rgba(206, 173, 128, 0.5)',
                    transition: 'width 0.1s linear',
                    position: 'relative',
                }}>
                    {/* Shimmer Effect */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                        transform: 'translateX(-100%)',
                        animation: 'shimmer 1.5s infinite',
                    }} />
                </div>
            </div>
            </>
        )}

        <nav style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
            background: navBg,
            backdropFilter: 'blur(20px)',
            borderBottom: navBorder,
            transition: 'all 0.3s ease',
            padding: '0 20px',
        }}>
            <div style={{ maxWidth: 1550, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>

                {/* Logo */}
                <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <img src="https://aakritishealverse.com/cdn/shop/files/Healverse_logo_1.png" alt="Logo"
                        style={{ width: 'clamp(100px, 25vw, 150px)', height: 'auto', maxHeight: 44 }} />
                </Link>

                {/* ──── DESKTOP actions (hidden on mobile) ──── */}
                <div className="nav-desktop" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>

                    {/* Language Picker */}
                    <div id="gt-pill" style={{ position: 'relative' }}>
                        <button onClick={() => setLangOpen(v => !v)} title="Translate page" style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 50,
                            border: isDark ? '1.5px solid rgba(99,179,237,0.3)' : '1.5px solid rgba(79,110,247,0.25)',
                            background: langOpen ? isDark ? 'rgba(99,179,237,0.18)' : 'rgba(79,110,247,0.13)' : isDark ? 'rgba(99,179,237,0.08)' : 'rgba(79,110,247,0.07)',
                            color: isDark ? '#7dd3fc' : '#3451d1', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.25s ease', whiteSpace: 'nowrap',
                        }}>
                            <FiGlobe size={14} /><span>{activeLang.native}</span>
                            <FiChevronDown size={12} style={{ transition: 'transform 0.25s', transform: langOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
                        </button>
                        {langOpen && (
                            <div style={{
                                position: 'absolute', top: 'calc(100% + 10px)', right: 0, width: 220,
                                background: T.surface, border: isDark ? '1px solid rgba(79,110,247,0.18)' : '1px solid rgba(79,110,247,0.14)',
                                borderRadius: 14, boxShadow: isDark ? '0 16px 48px rgba(0,0,0,0.55)' : '0 16px 48px rgba(79,110,247,0.12)',
                                overflow: 'hidden', zIndex: 2000, animation: 'fadeInUp 0.18s ease forwards',
                            }}>
                                <div style={{ padding: '10px 16px 8px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 7 }}>
                                    <FiGlobe size={13} style={{ color: isDark ? '#7dd3fc' : '#4f6ef7' }} />
                                    <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: 0.8 }}>Select Language</span>
                                </div>
                                <div style={{ maxHeight: 280, overflowY: 'auto', padding: '6px 0' }}>
                                    {LANGUAGES.map(lang => {
                                        const isActive = lang.code === activeLang.code;
                                        return (
                                            <button key={lang.code} onClick={() => handleLangSelect(lang)} style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                width: '100%', padding: '9px 16px',
                                                background: isActive ? isDark ? 'rgba(79,110,247,0.16)' : 'rgba(79,110,247,0.08)' : 'transparent',
                                                border: 'none', cursor: 'pointer', transition: 'background 0.15s', textAlign: 'left',
                                            }}>
                                                <div>
                                                    <div style={{ fontSize: 13, fontWeight: 600, color: isActive ? (isDark ? '#7dd3fc' : '#3451d1') : T.text }}>{lang.native}</div>
                                                    <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{lang.label}</div>
                                                </div>
                                                {isActive && <div style={{ width: 7, height: 7, borderRadius: '50%', background: isDark ? '#7dd3fc' : '#3451d1' }} />}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Guest links */}
                    {!user && (
                        <>
                            <Link to="/login" className="btn btn-outline btn-sm" style={{ textDecoration: 'none' }}>Login</Link>
                            <Link to="/register" className="btn btn-primary btn-sm" style={{ textDecoration: 'none' }}>Register</Link>
                        </>
                    )}

                    {/* Auth links */}
                    {user && (
                        <>
                            <Link to="/dashboard" className={`btn btn-sm ${location.pathname === '/dashboard' ? 'btn-primary' : 'btn-outline'}`}
                                style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <FiStar size={14} /> My Reports
                            </Link>
                            {user.role === 'admin' && (
                                <Link to="/admin" className={`btn btn-sm ${location.pathname === '/admin' ? 'btn-gold' : 'btn-outline'}`}
                                    style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <FiGrid size={14} /> Admin
                                </Link>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(79,110,247,0.07)', padding: '5px 12px', borderRadius: 50, border: `1px solid ${T.border}` }}>
                                <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'linear-gradient(135deg, #3451d1, #4f6ef7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff' }}>
                                    {user.firstName[0]}{user.lastName[0]}
                                </div>
                                <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{user.firstName}</span>
                            </div>
                            <button onClick={handleLogout} className="btn btn-sm btn-danger" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <FiLogOut size={14} /> Logout
                            </button>
                        </>
                    )}
                </div>

                {/* ──── MOBILE hamburger ──── */}
                <button className="nav-hamburger" onClick={() => setOpen(v => !v)}
                    style={{ display: 'none', flexDirection: 'column', justifyContent: 'center', gap: 5, width: 40, height: 40, background: 'none', border: 'none', cursor: 'pointer', padding: 8, borderRadius: 8 }}>
                    <span style={{ display: 'block', height: 2, borderRadius: 2, background: T.text, width: open ? '100%' : '100%', transform: open ? 'rotate(45deg) translate(5px, 5px)' : 'none', transition: 'all 0.25s' }} />
                    <span style={{ display: 'block', height: 2, borderRadius: 2, background: T.text, width: '80%', opacity: open ? 0 : 1, transition: 'all 0.25s' }} />
                    <span style={{ display: 'block', height: 2, borderRadius: 2, background: T.text, width: '100%', transform: open ? 'rotate(-45deg) translate(5px, -5px)' : 'none', transition: 'all 0.25s' }} />
                </button>
            </div>

            {/* ──── Mobile dropdown menu ──── */}
            {open && (
                <div className="nav-mobile-menu" style={{
                    background: isDark ? 'rgba(8,14,26,0.98)' : 'rgba(255,255,255,0.98)',
                    backdropFilter: 'blur(20px)',
                    borderTop: `1px solid ${T.border}`,
                    padding: '12px 20px 20px',
                    animation: 'fadeInUp 0.2s ease',
                }}>
                    {/* Language button — wrapped in .nav-lang-mobile so outside-click handler can whitelist it */}
                    <div className="nav-lang-mobile">
                    <button onClick={() => setLangOpen(v => !v)} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
                        padding: '13px 0', background: 'none', border: 'none', borderBottom: `1px solid ${T.border}`,
                        color: T.text, fontSize: 15, fontWeight: 600, cursor: 'pointer',
                    }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}><FiGlobe size={16} />{activeLang.native}</span>
                        <FiChevronDown size={14} style={{ transform: langOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                    </button>
                    {langOpen && (
                        <div style={{ padding: '8px 0', borderBottom: `1px solid ${T.border}`, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                            {LANGUAGES.map(lang => (
                                <button key={lang.code} onClick={() => handleLangSelect(lang)} style={{
                                    padding: '8px 12px', background: lang.code === activeLang.code ? (isDark ? 'rgba(79,110,247,0.18)' : 'rgba(79,110,247,0.08)') : 'transparent',
                                    border: 'none', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                                    color: lang.code === activeLang.code ? (isDark ? '#7dd3fc' : '#3451d1') : T.text,
                                    fontSize: 13, fontWeight: 600,
                                }}>
                                    {lang.native}
                                </button>
                            ))}
                        </div>
                    )}
                    </div>

                    {/* Guest links */}
                    {!user && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
                            <Link to="/login" className="btn btn-outline" style={{ textDecoration: 'none', textAlign: 'center' }}>Login</Link>
                            <Link to="/register" className="btn btn-primary" style={{ textDecoration: 'none', textAlign: 'center' }}>Register</Link>
                        </div>
                    )}

                    {/* Auth links */}
                    {user && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                            {/* User info */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0', borderBottom: `1px solid ${T.border}` }}>
                                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #3451d1, #4f6ef7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                                    {user.firstName[0]}{user.lastName[0]}
                                </div>
                                <div>
                                    <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{user.firstName} {user.lastName}</div>
                                    <div style={{ fontSize: 12, color: T.muted }}>{user.email}</div>
                                </div>
                            </div>

                            <Link to="/dashboard" onClick={() => setOpen(false)} style={{
                                display: 'flex', alignItems: 'center', gap: 10, padding: '13px 0',
                                textDecoration: 'none', color: location.pathname === '/dashboard' ? '#4f6ef7' : T.text,
                                fontWeight: 600, fontSize: 15, borderBottom: `1px solid ${T.border}`,
                            }}>
                                <FiStar size={16} /> My Reports
                            </Link>

                            {user.role === 'admin' && (
                                <Link to="/admin" onClick={() => setOpen(false)} style={{
                                    display: 'flex', alignItems: 'center', gap: 10, padding: '13px 0',
                                    textDecoration: 'none', color: location.pathname === '/admin' ? '#cead80' : T.text,
                                    fontWeight: 600, fontSize: 15, borderBottom: `1px solid ${T.border}`,
                                }}>
                                    <FiGrid size={16} /> Admin Panel
                                </Link>
                            )}

                            <button onClick={handleLogout} style={{
                                display: 'flex', alignItems: 'center', gap: 10, padding: '13px 0',
                                background: 'none', border: 'none', color: '#ef4444', fontWeight: 600, fontSize: 15, cursor: 'pointer', textAlign: 'left',
                            }}>
                                <FiLogOut size={16} /> Logout
                            </button>
                        </div>
                    )}
                </div>
            )}
        </nav>

        {/* CSS for desktop/mobile toggle and translation keyframes */}
        <style>{`
            @media (min-width: 769px) {
                .nav-desktop { display: flex !important; }
                .nav-hamburger { display: none !important; }
                .nav-mobile-menu { display: none !important; }
            }
            @media (max-width: 768px) {
                .nav-desktop { display: none !important; }
                .nav-hamburger { display: flex !important; }
            }

            /* Translation Overlay Animations */
            @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }
            @keyframes scaleIn {
                from { transform: scale(0.92); opacity: 0; }
                to { transform: scale(1); opacity: 1; }
            }
            @keyframes spin {
                to { transform: rotate(360deg); }
            }
            @keyframes pulse {
                0%, 100% { transform: scale(1); opacity: 1; }
                50% { transform: scale(1.06); opacity: 0.85; }
            }
            @keyframes shimmer {
                100% { transform: translateX(100%); }
            }
        `}</style>
        </>
    );
}
